import { createSelector } from 'reselect';
import _flatten from 'lodash/flatten';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _set from 'lodash/set';
import _uniq from 'lodash/uniq';
import _intersection from 'lodash/intersection';

import { MOET_CONSTANTS } from 'moet-core';

import { RETURN_RATES } from '@/constants';
import { categoryMapper } from '@/constants/mappers';
import {
  calculateFinalPaymentDelay,
  calculatePML,
  getReturnRateValue,
  hasHighPML,
  hasPMLCategoryOrBusinessConcept,
} from '@/utils/preAssessment';

import { getBusinessConcepts, getCountries, optionsNameSpace } from '../options';
import { getFrontendTypeFromQueryParam } from '../widget';
import { merchantNameSpace } from './common';

const { WIDGET_TYPE } = MOET_CONSTANTS;

export const preAssessmentDetails = createSelector(
  merchantNameSpace,
  ({ preAssessment = {} }) => preAssessment,
);

export const preAssessmentUnderwritingStatus = createSelector(
  merchantNameSpace,
  ({ underwritingStatus }) => underwritingStatus,
);

export const isPreAssessmentMerchant = createSelector(
  getFrontendTypeFromQueryParam,
  widgetType =>
    widgetType === WIDGET_TYPE.MANAGED_RISK || widgetType === WIDGET_TYPE.MANAGED_SME_RISK,
);

// return rates
export const getReturnRatesPerCountry = createSelector(
  optionsNameSpace,
  preAssessmentDetails,
  ({ returnRates = {} }, { sellingCountries = {}, goodsCategory }) => {
    const ratesPerCountry = returnRates[RETURN_RATES.RETURN_RATES_PER_COUNTRY] || [];
    const mappedCategory = categoryMapper[goodsCategory];
    const option = ratesPerCountry.find(
      elem => elem.countryCode === sellingCountries.main && elem.Category === mappedCategory,
    );
    const returnRate = _get(option, 'Return-rate');

    return getReturnRateValue(returnRate);
  },
);

export const getReturnRatesPerCategory = createSelector(
  optionsNameSpace,
  preAssessmentDetails,
  ({ returnRates = {} }, { goodsCategory }) => {
    const ratesPerCategory = returnRates[RETURN_RATES.RETURN_RATES_PER_CATEGORY] || [];
    const mappedCategory = categoryMapper[goodsCategory];
    const option = ratesPerCategory.find(elem => elem.Category === mappedCategory);

    const returnRate = _get(option, 'Return-rate');

    return getReturnRateValue(returnRate);
  },
);

export const getReturnRatesPerGroup = createSelector(
  optionsNameSpace,
  preAssessmentDetails,
  ({ returnRates = {} }, { goodsCategory }) => {
    const ratesPerGroup = returnRates[RETURN_RATES.RETURN_RATES_PER_GROUP] || [];
    // if the category in the spreadsheet is missing, we map it to the group
    const mappedGroup = categoryMapper[goodsCategory] || 'Klarna average RR';
    const option = ratesPerGroup.find(elem => elem.Group === mappedGroup);
    const returnRate = _get(option, 'Return-rate');

    return getReturnRateValue(returnRate);
  },
);

export const getOutstandingReturnsVariable = createSelector(
  optionsNameSpace,
  preAssessmentDetails,
  ({ returnRates = {} }, { goodsCategory }) => {
    const returnsVariableList = returnRates[RETURN_RATES.OUTSTANDING_RETURNS_VARIABLES] || [];
    const mappedCategory = categoryMapper[goodsCategory];
    const foundCategory = returnsVariableList.find(elem => elem.Category === mappedCategory);
    const foundGroup = returnsVariableList.find(elem => elem.Group === mappedCategory);
    const defaultValue = returnsVariableList.find(elem => elem.Category === 'Unknown');

    // check the "YET TO COME" column for category otherwise "Group avg yet-to-come" for group
    let returnsVariable;
    if (!_isEmpty(foundCategory)) {
      returnsVariable = _get(foundCategory, 'yet-to-come');
    } else if (!_isEmpty(foundGroup)) {
      returnsVariable = _get(foundGroup, 'Group-avg-yet-to-come');
    } else returnsVariable = _get(defaultValue, 'yet-to-come');

    return getReturnRateValue(returnsVariable);
  },
);

export const getReturnRate = createSelector(
  getReturnRatesPerCountry,
  getReturnRatesPerCategory,
  getReturnRatesPerGroup,
  (ratesPerCountry, ratesPerCategory, ratesPerGroup) => {
    return ratesPerCountry || ratesPerCategory || ratesPerGroup || 1;
  },
);

export const getQuestionnaires = createSelector(optionsNameSpace, ({ questionnaires = {} }) => {
  return questionnaires;
});

export const getBusinessConceptsFilteredByCountry = createSelector(
  preAssessmentDetails,
  getBusinessConcepts,
  ({ sellingCountries: { all = [] } = {} }, businessConcepts) => {
    const res = businessConcepts.filter(
      ({ countries }) => !countries || _intersection(all, countries).length,
    );
    return res;
  },
);

export const getQuestionnaire = questionnaireIdentifier =>
  createSelector(
    getQuestionnaires,
    preAssessmentDetails,
    (
      availableQuestionnaires = {},
      { products = [], sellingCountries = { all: [] }, questionnaires = {} },
    ) => {
      const questionnaire =
        questionnaires[questionnaireIdentifier] || availableQuestionnaires[questionnaireIdentifier];

      if (questionnaire) {
        const allProducts = _uniq(_flatten(products.map(({ products = [] }) => products)));
        const filteredQuestions = _get(questionnaire, 'questions', []).filter(
          ({ countries, products }) =>
            (!countries || countries.some(country => sellingCountries.all.includes(country))) &&
            (!products || products.some(product => allProducts.includes(product))),
        );

        _set(questionnaire, 'questions', filteredQuestions);
      }

      return questionnaire;
    },
  );

export const shouldRenderPMLQuestionnaires = createSelector(
  getReturnRate,
  getOutstandingReturnsVariable,
  isPreAssessmentMerchant,
  preAssessmentDetails,
  (
    returnRate,
    OutstandingReturnsVariable,
    isPreAssessmentMerchant = false,
    {
      businessConcepts = [],
      goodsCategory,
      yearlyAcquiredVolume,
      paymentDelay: originalPaymentDelay,
      paymentInterval,
    } = { businessConcepts: [] },
  ) => {
    const paymentDelay = calculateFinalPaymentDelay(originalPaymentDelay, paymentInterval);
    const isPMLCategory = hasPMLCategoryOrBusinessConcept(goodsCategory, businessConcepts);
    const hasExceededMaxValue = hasHighPML({
      yearlyAcquiredVolume,
      paymentDelay,
      returnRate,
      OutstandingReturnsVariable,
    });

    return isPreAssessmentMerchant && hasExceededMaxValue && isPMLCategory;
  },
);

export const getPML = createSelector(
  getReturnRate,
  getOutstandingReturnsVariable,
  isPreAssessmentMerchant,
  preAssessmentDetails,
  (
    returnRate,
    OutstandingReturnsVariable,
    isPreAssessmentMerchant = false,
    {
      businessConcepts = [],
      goodsCategory,
      yearlyAcquiredVolume,
      paymentDelay: originalPaymentDelay,
      paymentInterval,
    } = { businessConcepts: [] },
  ) => {
    const paymentDelay = calculateFinalPaymentDelay(originalPaymentDelay, paymentInterval);

    return calculatePML({
      yearlyAcquiredVolume,
      paymentDelay,
      returnRate,
      OutstandingReturnsVariable,
    });
  },
);

export const getExtendedExposure = createSelector(
  optionsNameSpace,
  ({ extendedExposure = {} }) => extendedExposure,
);

export const hasHighExposure = createSelector(
  preAssessmentDetails,
  ({ isHighExposure = false }) => isHighExposure,
);

export const getProductsFromPreAssessmentDetails = createSelector(
  getCountries,
  optionsNameSpace,
  preAssessmentDetails,
  (countries = [], { productNames = [] }, { products = [] }) =>
    products.map(({ country, products = [] }) => ({
      country: countries.find(({ key }) => key === country),
      products: products.map(({ product, channels }) => ({
        ...productNames.find(({ key }) => key === product),
        channels: channels.sort((a, b) => a.localeCompare(b)),
      })),
    })),
);

export const isLargeExposureMerchant = createSelector(
  getPML,
  preAssessmentDetails,
  (pml, { yearlyAcquiredVolume }) => {
    return pml >= 10000000 || yearlyAcquiredVolume >= 250000000;
  },
);
